import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { deleteMetaAdsCampaign } from "./useMetaAdsApi";

const GRAPH_CAMPAIGNS_KEY = ["meta-ads-graph-campaigns"];

/**
 * Removes one campaign from live list cache shapes from `fetchMetaAdsAdAccountCampaigns`.
 * Adjusts aggregate spend when per-row sums are present (aligns KPI cards with table).
 *
 * @param {unknown} data
 * @param {string} campaignId
 */
function stripCampaignFromGraphCampaignsPayload(data, campaignId) {
  const id = String(campaignId ?? "").trim();
  if (!data || typeof data !== "object" || !Array.isArray(data.campaigns)) {
    return data;
  }
  const campaigns = data.campaigns.filter(
    (c) => String(c?.campaignId ?? "") !== id,
  );
  if (campaigns.length === data.campaigns.length) {
    return data;
  }
  const adSets = Array.isArray(data.adSets)
    ? data.adSets.filter((adSet) => String(adSet?.campaignId ?? "") !== id)
    : data.adSets;
  const ads = Array.isArray(data.ads)
    ? data.ads.filter((ad) => String(ad?.campaignId ?? "") !== id)
    : data.ads;
  let totalSpendCents = data.totalSpendCents;
  const removed = data.campaigns.find(
    (c) => String(c?.campaignId ?? "") === id,
  );
  if (
    removed &&
    typeof removed.spendCentsSum === "number" &&
    typeof totalSpendCents === "number"
  ) {
    totalSpendCents = Math.max(0, totalSpendCents - removed.spendCentsSum);
  }
  return { ...data, campaigns, adSets, ads, totalSpendCents };
}

/**
 * Permanently delete a campaign (Meta Graph DELETE + mirror row in Postgres when present).
 * After DELETE succeeds, updates cached ad-account campaign lists synchronously so the row goes away
 * without waiting for another `GET …/ad-account/campaigns` refetch (no stale row flash).
 */
export function useMetaAdsDeleteCampaign() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation("metaAds");

  return useMutation({
    mutationFn: async ({ campaignId }) => {
      await deleteMetaAdsCampaign(campaignId);
      return { campaignId: String(campaignId ?? "").trim() };
    },

    onError: (err) => {
      showToast(err?.message || t("create.deleteCampaignErrorToast"), "error");
    },

    onSuccess: (_data, variables) => {
      const id = String(variables?.campaignId ?? "").trim();
      if (id) {
        queryClient.removeQueries({
          queryKey: ["meta-ads-campaign-detail", id],
          exact: true,
        });
        queryClient.setQueriesData(
          { queryKey: GRAPH_CAMPAIGNS_KEY, exact: false },
          (old) => stripCampaignFromGraphCampaignsPayload(old, id),
        );
      }
      showToast(t("create.deleteCampaignSuccessToast"), "success");
    },
  });
}
